import React, { useState } from 'react';
import GenericModal from '../../components/agv-generic-modal';
import { MODAL_SIZE, MODAL_SIZE_STYLE } from '../../enums/modal-size.enum';
import Stepper from '../../components/agv-stepper';
import VehicleGeneralInfoStep from './steps/vehicle-general-info';
import VehicleContainersStep from './steps/vehicle-containers';
import usePostVehicle from '../../api/vehicles/usePostVehicle';

export default function CreateVehicleModal({ isOpen, setIsOpen, onSuccess }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [vehicleData, setVehicleData] = useState({
    // Champs requis
    CAMION: '',
    LIBCAM: '',
    IMMAT: '',
    TELCAM: '',
    LIBTYP: '',
    TRANSP: '',
    factory_id: '',
    color: '#3B82F6', // Bleu par défaut
    // Containers (jusqu'à 10)
    VCASCA_01: '',
    VCASCA_02: '',
    VCASCA_03: '',
    VCASCA_04: '',
    VCASCA_05: '',
    VCASCA_06: '',
    VCASCA_07: '',
    VCASCA_08: '',
    VCASCA_09: '',
    VCASCA_10: '',
  });

  const STEPS = [
    { label: 'Informations générales', component: VehicleGeneralInfoStep },
    { 
      label: vehicleData.LIBTYP === 'Citerne compartimentée' ? 'Compartiments' : 'Capacité', 
      component: VehicleContainersStep 
    },
  ];

  const { isLoading, postVehicle } = usePostVehicle();

  const handleClose = () => {
    setIsOpen(false);
    setCurrentStep(0);
    setVehicleData({
      CAMION: '',
      LIBCAM: '',
      IMMAT: '',
      TELCAM: '',
      LIBTYP: '',
      TRANSP: '',
      factory_id: '',
      color: '#3B82F6',
      VCASCA_01: '',
      VCASCA_02: '',
      VCASCA_03: '',
      VCASCA_04: '',
      VCASCA_05: '',
      VCASCA_06: '',
      VCASCA_07: '',
      VCASCA_08: '',
      VCASCA_09: '',
      VCASCA_10: '',
    });
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      const isCiterneCompartimentee = vehicleData.LIBTYP === 'Citerne compartimentée';
      
      // Calculer la capacité totale à partir des containers
      const totalCapacity = [
        vehicleData.VCASCA_01,
        vehicleData.VCASCA_02,
        vehicleData.VCASCA_03,
        vehicleData.VCASCA_04,
        vehicleData.VCASCA_05,
        vehicleData.VCASCA_06,
        vehicleData.VCASCA_07,
        vehicleData.VCASCA_08,
        vehicleData.VCASCA_09,
        vehicleData.VCASCA_10,
      ]
        .filter(Boolean)
        .reduce((sum, value) => sum + parseFloat(value), 0);

      // Convertir les champs numériques en nombres et ajouter les valeurs par défaut
      const formattedData = {
        ...vehicleData,
        CAMION: parseInt(vehicleData.CAMION, 10),
        // Champs supprimés du formulaire mais requis par l'API - valeurs par défaut
        NIMMCE: vehicleData.IMMAT || '', // Utilise l'immat comme NIMMCE
        TRPEXT: 1, // Valeur par défaut
        SITAFF: 1, // Valeur par défaut
        NOGRIL: 1, // Valeur par défaut
        LIBGRI: 'Standard', // Valeur par défaut
        CAPSAC: totalCapacity, // Calculé à partir des containers
        CAPVRA: undefined, // Optionnel
        CAL44T: 'N', // Valeur par défaut
        CNAVETTE: 'N', // Valeur par défaut
        // Factory ID (optionnel)
        factory_id: vehicleData.factory_id ? parseInt(vehicleData.factory_id, 10) : undefined,
        // Couleur du véhicule
        color: vehicleData.color,
      };

      // Pour citerne compartimentée, envoyer tous les containers remplis
      if (isCiterneCompartimentee) {
        formattedData.VCASCA_01 = parseFloat(vehicleData.VCASCA_01);
        formattedData.VCASCA_02 = vehicleData.VCASCA_02 ? parseFloat(vehicleData.VCASCA_02) : undefined;
        formattedData.VCASCA_03 = vehicleData.VCASCA_03 ? parseFloat(vehicleData.VCASCA_03) : undefined;
        formattedData.VCASCA_04 = vehicleData.VCASCA_04 ? parseFloat(vehicleData.VCASCA_04) : undefined;
        formattedData.VCASCA_05 = vehicleData.VCASCA_05 ? parseFloat(vehicleData.VCASCA_05) : undefined;
        formattedData.VCASCA_06 = vehicleData.VCASCA_06 ? parseFloat(vehicleData.VCASCA_06) : undefined;
        formattedData.VCASCA_07 = vehicleData.VCASCA_07 ? parseFloat(vehicleData.VCASCA_07) : undefined;
        formattedData.VCASCA_08 = vehicleData.VCASCA_08 ? parseFloat(vehicleData.VCASCA_08) : undefined;
        formattedData.VCASCA_09 = vehicleData.VCASCA_09 ? parseFloat(vehicleData.VCASCA_09) : undefined;
        formattedData.VCASCA_10 = vehicleData.VCASCA_10 ? parseFloat(vehicleData.VCASCA_10) : undefined;
      } else {
        // Pour les autres types, n'envoyer que VCASCA_01 (capacité unique)
        formattedData.VCASCA_01 = parseFloat(vehicleData.VCASCA_01);
        formattedData.VCASCA_02 = undefined;
        formattedData.VCASCA_03 = undefined;
        formattedData.VCASCA_04 = undefined;
        formattedData.VCASCA_05 = undefined;
        formattedData.VCASCA_06 = undefined;
        formattedData.VCASCA_07 = undefined;
        formattedData.VCASCA_08 = undefined;
        formattedData.VCASCA_09 = undefined;
        formattedData.VCASCA_10 = undefined;
      }

      await postVehicle(formattedData);
      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Erreur lors de la création du véhicule:', error);
    }
  };

  const isStepValid = () => {
    if (currentStep === 0) {
      // Vérifier que les champs requis de l'étape 1 sont remplis
      return (
        vehicleData.CAMION &&
        vehicleData.LIBCAM &&
        vehicleData.IMMAT &&
        vehicleData.TELCAM &&
        vehicleData.LIBTYP &&
        vehicleData.TRANSP
      );
    } else if (currentStep === 1) {
      // Vérifier qu'au moins un container est rempli
      return vehicleData.VCASCA_01;
    }
    return true;
  };

  const StepComponent = STEPS[currentStep].component;

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={handleClose}
      title="Créer un nouveau véhicule"
      mainAction={currentStep === STEPS.length - 1 ? handleSubmit : handleNext}
      mainActionText={currentStep === STEPS.length - 1 ? 'Créer' : 'Suivant'}
      mainActionDisabled={!isStepValid()}
      mainActionIsLoading={isLoading}
      secondaryAction={currentStep === 0 ? handleClose : handlePrevious}
      secondaryActionText={currentStep === 0 ? 'Annuler' : 'Précédent'}
      size={MODAL_SIZE_STYLE[MODAL_SIZE.XL]}
    >
      <Stepper steps={STEPS} currentStep={currentStep} />
      <StepComponent
        vehicleData={vehicleData}
        setVehicleData={setVehicleData}
      />
    </GenericModal>
  );
}

